import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Checkbox,
  IconButton,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  LinearProgress,
  Grid,
  Card,
  CardContent,
  Tooltip,
  Divider,
  Autocomplete,
  FormControlLabel,
  Switch,
  Menu,
  ListItemButton
} from '@mui/material'
import {
  CheckCircle,
  Error as ErrorIcon,
  Warning,
  Add,
  Edit,
  Delete,
  ArrowBack,
  EscalatorWarning,
  AttachFile,
  Comment,
  History,
  MoreVert,
  FilterList,
  Download,
  Print,
  Refresh,
  Assignment,
  Schedule,
  Person,
  Close
} from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import { useAuth } from '../../../contexts/Authcontexts'
import api from '../../../services/api'
import { format, isAfter, isBefore, addDays } from 'date-fns'

const Checklist = ({ fileId, onBack }) => {
  const { enqueueSnackbar } = useSnackbar()
  const { user, hasPermission } = useAuth()
  
  const [loading, setLoading] = useState(false)
  const [checklist, setChecklist] = useState([])
  const [filteredChecklist, setFilteredChecklist] = useState([])
  const [fileInfo, setFileInfo] = useState(null)
  const [statistics, setStatistics] = useState(null)
  const [deficiencyMasters, setDeficiencyMasters] = useState([])
  const [users, setUsers] = useState([])
  
  // Dialog states
  const [openDialog, setOpenDialog] = useState(false)
  const [openEscalateDialog, setOpenEscalateDialog] = useState(false)
  const [openEditDialog, setOpenEditDialog] = useState(false)
  const [openHistoryDialog, setOpenHistoryDialog] = useState(false)
  const [openWaiveDialog, setOpenWaiveDialog] = useState(false)
  
  const [selectedItem, setSelectedItem] = useState(null)
  const [itemHistory, setItemHistory] = useState([])
  
  // Form data states
  const [formData, setFormData] = useState({
    deficiencyId: '',
    remarks: '',
    expectedResolutionDate: '',
    hasDocument: false,
    priority: 'MEDIUM'
  })
  const [escalateData, setEscalateData] = useState({
    escalatedTo: '',
    escalationReason: ''
  })
  const [editData, setEditData] = useState({
    remarks: '',
    expectedResolutionDate: '',
    hasDocument: false
  })
  const [waiveData, setWaiveData] = useState({
    waiveReason: ''
  })
  
  // Filter states
  const [filters, setFilters] = useState({
    status: 'ALL',
    category: 'ALL',
    severity: 'ALL',
    overdue: false,
    showCompleted: true
  })
  
  // Menu states
  const [anchorEl, setAnchorEl] = useState(null)
  const [menuItem, setMenuItem] = useState(null)

  useEffect(() => {
    if (fileId) {
      loadChecklist()
      loadDeficiencyMasters()
      loadUsers()
    }
  }, [fileId])

  useEffect(() => {
    applyFilters()
  }, [checklist, filters])

  const loadChecklist = async () => {
    console.log('Loading checklist for fileId:', fileId)
    setLoading(true)
    try {
      const response = await api.get(`/mrd/deficiency/${fileId}`, {
        params: { includeCompleted: true }
      })
      console.log('Checklist response:', response.data)
      
      if (response.data.success) {
        const data = response.data.data
        setFileInfo(data.file)
        setChecklist(data.deficiencyChecklist || [])
        setStatistics(data.statistics)
        console.log('Checklist loaded:', data.deficiencyChecklist?.length, 'items')
      }
    } catch (error) {
      console.error('Load checklist error:', error)
      enqueueSnackbar('Failed to load checklist', { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const loadDeficiencyMasters = async () => {
    try {
      const response = await api.get('/mrd/deficiency/master', {
        params: { 
          isActive: true,
          limit: 100  // Get more masters for manual selection
        }
      })
      console.log('Deficiency masters response:', response.data)
      
      const masters = response.data.data?.docs || response.data.data || []
      console.log('Deficiency masters loaded:', masters.length, 'masters')
      setDeficiencyMasters(Array.isArray(masters) ? masters : [])
    } catch (error) {
      console.error('Load deficiency masters error:', error)
    }
  }

  const loadUsers = async () => {
    try {
      const response = await api.get('/api/users', {
        params: { isActive: true, role: 'MRD_STAFF,MRD_INCHARGE,ADMIN' }
      })
      const userData = response.data.data?.docs || response.data.data || []
      setUsers(Array.isArray(userData) ? userData : [])
    } catch (error) {
      console.error('Load users error:', error)
    }
  }

  const applyFilters = () => {
    let filtered = [...checklist]

    // Filter by status
    if (filters.status !== 'ALL') {
      filtered = filtered.filter(item => 
        item.checklistItem?.status === filters.status
      )
    }

    // Filter by category
    if (filters.category !== 'ALL') {
      filtered = filtered.filter(item => item.deficiency.category === filters.category)
    }

    // Filter by severity
    if (filters.severity !== 'ALL') {
      filtered = filtered.filter(item => item.deficiency.severity === filters.severity)
    }

    // Filter overdue items
    if (filters.overdue) {
      filtered = filtered.filter(item => item.checklistItem?.isOverdue)
    }

    // Show/hide completed items
    if (!filters.showCompleted) {
      filtered = filtered.filter(item => item.checklistItem?.status !== 'COMPLETED')
    }

    setFilteredChecklist(filtered)
  }

  const handleToggleComplete = async (item, isOk) => {
    if (!item.checklistItem) return
    
    try {
      await api.put(`/mrd/deficiency/item/${item.checklistItem._id}`, {
        isOk,
        hasDocument: item.checklistItem.hasDocument,
        actualResolutionDate: isOk ? new Date().toISOString() : null
      })
      enqueueSnackbar(
        isOk ? 'Item marked as completed' : 'Item marked as pending',
        { variant: 'success' }
      )
      loadChecklist()
    } catch (error) {
      console.error('Toggle complete error:', error)
      enqueueSnackbar('Failed to update item', { variant: 'error' })
    }
  }

  const handleAddDeficiency = async () => {
    console.log('Add deficiency called with:', { fileId, formData })
    
    if (!formData.deficiencyId) {
      enqueueSnackbar('Please select a deficiency type', { variant: 'warning' })
      return
    }

    try {
      console.log('Making API call to:', `/mrd/deficiency/${fileId}`)
      console.log('With data:', formData)
      
      const response = await api.post(`/mrd/deficiency/${fileId}`, formData)
      console.log('API response:', response.data)
      
      enqueueSnackbar('Deficiency added successfully', { variant: 'success' })
      setOpenDialog(false)
      resetFormData()
      loadChecklist()
    } catch (error) {
      console.error('Add deficiency error:', error)
      console.error('Error response:', error.response?.data)
      enqueueSnackbar(
        error.response?.data?.message || 'Failed to add deficiency',
        { variant: 'error' }
      )
    }
  }

  const handleEditDeficiency = async () => {
    if (!selectedItem?.checklistItem) return

    try {
      await api.put(`/mrd/deficiency/item/${selectedItem.checklistItem._id}`, editData)
      enqueueSnackbar('Deficiency updated successfully', { variant: 'success' })
      setOpenEditDialog(false)
      setSelectedItem(null)
      loadChecklist()
    } catch (error) {
      console.error('Edit deficiency error:', error)
      enqueueSnackbar('Failed to update deficiency', { variant: 'error' })
    }
  }

  const handleWaiveDeficiency = async () => {
    if (!selectedItem?.checklistItem || !waiveData.waiveReason) {
      enqueueSnackbar('Please provide a reason for waiving', { variant: 'warning' })
      return
    }

    try {
      await api.put(`/mrd/deficiency/item/${selectedItem.checklistItem._id}`, {
        status: 'WAIVED',
        waiveReason: waiveData.waiveReason,
        waivedBy: user._id,
        waivedAt: new Date().toISOString()
      })
      enqueueSnackbar('Deficiency waived successfully', { variant: 'success' })
      setOpenWaiveDialog(false)
      setSelectedItem(null)
      setWaiveData({ waiveReason: '' })
      loadChecklist()
    } catch (error) {
      console.error('Waive deficiency error:', error)
      enqueueSnackbar('Failed to waive deficiency', { variant: 'error' })
    }
  }

  const handleDeleteDeficiency = async (item) => {
    if (!item.checklistItem) return

    if (!window.confirm('Are you sure you want to delete this deficiency item?')) {
      return
    }

    try {
      await api.delete(`/mrd/deficiency/item/${item.checklistItem._id}`)
      enqueueSnackbar('Deficiency deleted successfully', { variant: 'success' })
      loadChecklist()
    } catch (error) {
      console.error('Delete deficiency error:', error)
      enqueueSnackbar('Failed to delete deficiency', { variant: 'error' })
    }
  }

  const handleViewHistory = async (item) => {
    if (!item.checklistItem) return

    try {
      const response = await api.get(`/mrd/deficiency/item/${item.checklistItem._id}/history`)
      setItemHistory(response.data.data || [])
      setSelectedItem(item)
      setOpenHistoryDialog(true)
    } catch (error) {
      console.error('Load history error:', error)
      enqueueSnackbar('Failed to load history', { variant: 'error' })
    }
  }

  const resetFormData = () => {
    setFormData({
      deficiencyId: '',
      remarks: '',
      expectedResolutionDate: '',
      hasDocument: false,
      priority: 'MEDIUM'
    })
  }

  const handleMenuOpen = (event, item) => {
    setAnchorEl(event.currentTarget)
    setMenuItem(item)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
    setMenuItem(null)
  }

  const handleAssignDeficiency = async (item, assignedTo) => {
    if (!item.checklistItem) return

    try {
      await api.put(`/mrd/deficiency/item/${item.checklistItem._id}`, {
        assignedTo,
        status: 'IN_PROGRESS'
      })
      enqueueSnackbar('Deficiency assigned successfully', { variant: 'success' })
      loadChecklist()
    } catch (error) {
      console.error('Assign deficiency error:', error)
      enqueueSnackbar('Failed to assign deficiency', { variant: 'error' })
    }
  }

  const handleEscalate = async () => {
    if (!selectedItem || !escalateData.escalatedTo || !escalateData.escalationReason) {
      enqueueSnackbar('Please fill all required fields', { variant: 'warning' })
      return
    }

    try {
      await api.post(`/mrd/deficiency/escalate/${selectedItem.checklistItem._id}`, escalateData)
      enqueueSnackbar('Deficiency escalated successfully', { variant: 'success' })
      setOpenEscalateDialog(false)
      setSelectedItem(null)
      setEscalateData({ escalatedTo: '', escalationReason: '' })
      loadChecklist()
    } catch (error) {
      console.error('Escalate error:', error)
      enqueueSnackbar('Failed to escalate deficiency', { variant: 'error' })
    }
  }

  const getStatusIcon = (item) => {
    if (item.checklistItem?.status === 'COMPLETED') {
      return <CheckCircle color="success" />
    } else if (item.checklistItem?.isOverdue) {
      return <ErrorIcon color="error" />
    } else if (item.deficiency?.severity === 'HIGH') {
      return <Warning color="error" />
    } else {
      return <Warning color="warning" />
    }
  }

  const getStatusChip = (status) => {
    const config = {
      PENDING: { color: 'warning', label: 'Pending' },
      IN_PROGRESS: { color: 'info', label: 'In Progress' },
      COMPLETED: { color: 'success', label: 'Completed' },
      ESCALATED: { color: 'error', label: 'Escalated' },
      WAIVED: { color: 'default', label: 'Waived' }
    }
    
    const { color, label} = config[status] || { color: 'default', label: status }
    
    return (
      <Chip
        label={label}
        color={color}
        size="small"
      />
    )
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
        <Box>
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Quality Checklist Management
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
            Add and manage deficiencies from the master list for comprehensive quality control
          </Typography>
          <Alert severity="info" sx={{ mt: 2, maxWidth: 600 }}>
            <strong>Manual Mode:</strong> Select any deficiency from the master list to add to this file's checklist. 
            Track progress, assign responsibilities, and ensure quality compliance.
          </Alert>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadChecklist}
            size="large"
          >
            Refresh
          </Button>
          {hasPermission('MRD_STAFF') && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setOpenDialog(true)}
              size="large"
            >
              Add Deficiency
            </Button>
          )}
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <FilterList />
          Filter & Search Options
        </Typography>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} sm={6} lg={2}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                label="Status"
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              >
                <MenuItem value="ALL">All Status</MenuItem>
                <MenuItem value="PENDING">Pending</MenuItem>
                <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
                <MenuItem value="COMPLETED">Completed</MenuItem>
                <MenuItem value="ESCALATED">Escalated</MenuItem>
                <MenuItem value="WAIVED">Waived</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} lg={2}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={filters.category}
                label="Category"
                onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
              >
                <MenuItem value="ALL">All Categories</MenuItem>
                <MenuItem value="MANDATORY">Mandatory</MenuItem>
                <MenuItem value="IMPORTANT">Important</MenuItem>
                <MenuItem value="OPTIONAL">Optional</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} lg={2}>
            <FormControl fullWidth>
              <InputLabel>Severity</InputLabel>
              <Select
                value={filters.severity}
                label="Severity"
                onChange={(e) => setFilters(prev => ({ ...prev, severity: e.target.value }))}
              >
                <MenuItem value="ALL">All Severity</MenuItem>
                <MenuItem value="HIGH">High</MenuItem>
                <MenuItem value="MEDIUM">Medium</MenuItem>
                <MenuItem value="LOW">Low</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} lg={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={filters.overdue}
                  onChange={(e) => setFilters(prev => ({ ...prev, overdue: e.target.checked }))}
                  color="error"
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Warning fontSize="small" color={filters.overdue ? 'error' : 'disabled'} />
                  Overdue Only
                </Box>
              }
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={filters.showCompleted}
                  onChange={(e) => setFilters(prev => ({ ...prev, showCompleted: e.target.checked }))}
                  color="success"
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CheckCircle fontSize="small" color={filters.showCompleted ? 'success' : 'disabled'} />
                  Show Completed
                </Box>
              }
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={2}>
            <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'primary.50', borderRadius: 1 }}>
              <Typography variant="h6" color="primary" fontWeight={600}>
                {filteredChecklist.length}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                of {checklist.length} items
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Statistics Cards */}
      {statistics && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={6} sm={4} lg={1.5}>
            <Card sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="h3" color="primary" fontWeight={700}>
                  {statistics.total}
                </Typography>
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  Total Items
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} lg={1.5}>
            <Card sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="h3" color="warning.main" fontWeight={700}>
                  {statistics.pending || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  Pending
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} lg={1.5}>
            <Card sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="h3" color="info.main" fontWeight={700}>
                  {statistics.inProgress || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  In Progress
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} lg={1.5}>
            <Card sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="h3" color="success.main" fontWeight={700}>
                  {statistics.completed || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  Completed
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} lg={1.5}>
            <Card sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="h3" color="error.main" fontWeight={700}>
                  {statistics.overdue || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  Overdue
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} lg={1.5}>
            <Card sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="h3" color="secondary.main" fontWeight={700}>
                  {statistics.escalated || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  Escalated
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} lg={1.5}>
            <Card sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="h3" color="grey.600" fontWeight={700}>
                  {statistics.waived || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  Waived
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} lg={1.5}>
            <Card sx={{ height: '100%', borderRadius: 2, bgcolor: 'primary.50', border: '2px solid', borderColor: 'primary.200' }}>
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="h3" color="primary.main" fontWeight={700}>
                  {statistics.completionRate}%
                </Typography>
                <Typography variant="body2" color="primary.dark" fontWeight={600}>
                  Completion Rate
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Checklist */}
      <Paper sx={{ borderRadius: 2 }}>
        {filteredChecklist.length === 0 ? (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <Alert severity="info" sx={{ maxWidth: 600, mx: 'auto' }}>
              {checklist.length === 0 
                ? "No deficiencies added yet. Click 'Add Deficiency' to start adding items from the master list."
                : "No items match the current filters. Try adjusting your filter criteria."
              }
            </Alert>
          </Box>
        ) : (
          <List sx={{ p: 2 }}>
            {filteredChecklist.map((item, index) => (
              <ListItem
                key={item.deficiency._id}
                divider={index < filteredChecklist.length - 1}
                sx={{
                  bgcolor: item.checklistItem?.status === 'COMPLETED' ? 'success.50' : 
                           item.checklistItem?.status === 'WAIVED' ? 'grey.100' :
                           item.checklistItem?.isOverdue ? 'error.50' : 'inherit'
                }}
              >
                <ListItemIcon>
                  {getStatusIcon(item)}
                </ListItemIcon>
                
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={item.deficiency.code}
                        size="small"
                        variant="outlined"
                      />
                      <Typography
                        variant="body1"
                        sx={{
                          textDecoration: item.checklistItem?.status === 'COMPLETED' ? 'line-through' : 'none'
                        }}
                      >
                        {item.deficiency.description}
                      </Typography>
                      <Chip
                        label={item.deficiency.severity}
                        size="small"
                        color={
                          item.deficiency.severity === 'HIGH' ? 'error' :
                          item.deficiency.severity === 'MEDIUM' ? 'warning' : 'default'
                        }
                        variant="outlined"
                      />
                      <Chip
                        label={item.deficiency.category}
                        size="small"
                        color={item.deficiency.category === 'MANDATORY' ? 'error' : 'default'}
                      />
                    </Box>
                  }
                  secondary={
                    item.checklistItem ? (
                      <Box>
                        {item.checklistItem.remarks && (
                          <Typography variant="body2" color="text.secondary">
                            {item.checklistItem.remarks}
                          </Typography>
                        )}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                          {getStatusChip(item.checklistItem.status)}
                          {item.checklistItem.expectedResolutionDate && (
                            <Typography variant="caption" color="text.secondary">
                              Due: {format(new Date(item.checklistItem.expectedResolutionDate), 'MMM dd, yyyy')}
                            </Typography>
                          )}
                          {item.checklistItem.resolvedBy && (
                            <Typography variant="caption" color="text.secondary">
                              Resolved by: {item.checklistItem.resolvedBy.name}
                            </Typography>
                          )}
                          {item.checklistItem.isOverdue && (
                            <Chip label="OVERDUE" size="small" color="error" />
                          )}
                        </Box>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Not yet added to checklist
                      </Typography>
                    )
                  }
                />
                
                <ListItemSecondaryAction>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {item.checklistItem && (
                      <>
                        <Tooltip title="Mark as Complete">
                          <Checkbox
                            checked={item.checklistItem.status === 'COMPLETED'}
                            onChange={(e) => handleToggleComplete(item, e.target.checked)}
                            color="success"
                            disabled={item.checklistItem.status === 'WAIVED'}
                          />
                        </Tooltip>
                        
                        {item.checklistItem.status !== 'COMPLETED' && item.checklistItem.status !== 'WAIVED' && (
                          <Tooltip title="Escalate">
                            <IconButton 
                              size="small"
                              color="error"
                              onClick={() => {
                                setSelectedItem(item)
                                setOpenEscalateDialog(true)
                              }}
                            >
                              <EscalatorWarning fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        
                        <Tooltip title="More Actions">
                          <IconButton 
                            size="small"
                            onClick={(e) => handleMenuOpen(e, item)}
                          >
                            <MoreVert fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    

                  </Box>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </Paper>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          setSelectedItem(menuItem)
          setEditData({
            remarks: menuItem?.checklistItem?.remarks || '',
            expectedResolutionDate: menuItem?.checklistItem?.expectedResolutionDate?.split('T')[0] || '',
            hasDocument: menuItem?.checklistItem?.hasDocument || false
          })
          setOpenEditDialog(true)
          handleMenuClose()
        }}>
          <Edit sx={{ mr: 1 }} fontSize="small" />
          Edit
        </MenuItem>
        
        <MenuItem onClick={() => {
          handleViewHistory(menuItem)
          handleMenuClose()
        }}>
          <History sx={{ mr: 1 }} fontSize="small" />
          View History
        </MenuItem>
        
        {menuItem?.checklistItem?.status !== 'WAIVED' && menuItem?.checklistItem?.status !== 'COMPLETED' && (
          <MenuItem onClick={() => {
            setSelectedItem(menuItem)
            setOpenWaiveDialog(true)
            handleMenuClose()
          }}>
            <Close sx={{ mr: 1 }} fontSize="small" />
            Waive
          </MenuItem>
        )}
        
        {hasPermission('MRD_INCHARGE') && (
          <MenuItem 
            onClick={() => {
              handleDeleteDeficiency(menuItem)
              handleMenuClose()
            }}
            sx={{ color: 'error.main' }}
          >
            <Delete sx={{ mr: 1 }} fontSize="small" />
            Delete
          </MenuItem>
        )}
      </Menu>

      {/* Add Deficiency Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h6" fontWeight={600}>
            Add Deficiency from Master List
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Select any deficiency from the master list to add to this file
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Select Deficiency from Master List</InputLabel>
              <Select
                value={formData.deficiencyId}
                label="Select Deficiency from Master List"
                onChange={(e) => setFormData(prev => ({ ...prev, deficiencyId: e.target.value }))}
              >
                {deficiencyMasters.map((master) => (
                  <MenuItem key={master._id} value={master._id}>
                    <Box sx={{ width: '100%' }}>
                      <Typography variant="body2" fontWeight={600}>
                        {master.code} - {master.description}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Category: {master.category} | Severity: {master.severity} | Department: {master.department?.name || 'General'}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Priority</InputLabel>
              <Select
                value={formData.priority}
                label="Priority"
                onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
              >
                <MenuItem value="HIGH">High Priority</MenuItem>
                <MenuItem value="MEDIUM">Medium Priority</MenuItem>
                <MenuItem value="LOW">Low Priority</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Remarks"
              value={formData.remarks}
              onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))}
              sx={{ mb: 3 }}
              placeholder="Additional notes or comments..."
            />
            
            <TextField
              fullWidth
              type="date"
              label="Expected Resolution Date"
              value={formData.expectedResolutionDate}
              onChange={(e) => setFormData(prev => ({ ...prev, expectedResolutionDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 3 }}
              helperText="Leave empty to use default resolution time from deficiency master"
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={formData.hasDocument}
                  onChange={(e) => setFormData(prev => ({ ...prev, hasDocument: e.target.checked }))}
                />
              }
              label="Has Supporting Document"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenDialog(false)
            resetFormData()
          }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleAddDeficiency}>
            Add Deficiency
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Deficiency Dialog */}
      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Deficiency</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {selectedItem && (
              <Alert severity="info" sx={{ mb: 3 }}>
                Editing: {selectedItem.deficiency.code} - {selectedItem.deficiency.description}
              </Alert>
            )}
            
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Remarks"
              value={editData.remarks}
              onChange={(e) => setEditData(prev => ({ ...prev, remarks: e.target.value }))}
              sx={{ mb: 3 }}
            />
            
            <TextField
              fullWidth
              type="date"
              label="Expected Resolution Date"
              value={editData.expectedResolutionDate}
              onChange={(e) => setEditData(prev => ({ ...prev, expectedResolutionDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 3 }}
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={editData.hasDocument}
                  onChange={(e) => setEditData(prev => ({ ...prev, hasDocument: e.target.checked }))}
                />
              }
              label="Has Supporting Document"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleEditDeficiency}>
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Waive Deficiency Dialog */}
      <Dialog open={openWaiveDialog} onClose={() => setOpenWaiveDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Waive Deficiency</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {selectedItem && (
              <Alert severity="warning" sx={{ mb: 3 }}>
                Waiving: {selectedItem.deficiency.description}
              </Alert>
            )}
            
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Waive Reason"
              value={waiveData.waiveReason}
              onChange={(e) => setWaiveData(prev => ({ ...prev, waiveReason: e.target.value }))}
              placeholder="Explain why this deficiency should be waived..."
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenWaiveDialog(false)}>
            Cancel
          </Button>
          <Button variant="contained" color="warning" onClick={handleWaiveDeficiency}>
            Waive Deficiency
          </Button>
        </DialogActions>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={openHistoryDialog} onClose={() => setOpenHistoryDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Deficiency History</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {selectedItem && (
              <Alert severity="info" sx={{ mb: 3 }}>
                History for: {selectedItem.deficiency.code} - {selectedItem.deficiency.description}
              </Alert>
            )}
            
            {itemHistory.length === 0 ? (
              <Typography color="text.secondary">No history available</Typography>
            ) : (
              <List>
                {itemHistory.map((entry, index) => (
                  <ListItem key={index} divider={index < itemHistory.length - 1}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1" fontWeight={600}>
                            {entry.action}
                          </Typography>
                          <Chip label={entry.status} size="small" />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {entry.remarks}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {format(new Date(entry.timestamp), 'MMM dd, yyyy HH:mm')} by {entry.user?.name}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenHistoryDialog(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Escalate Dialog */}
      <Dialog open={openEscalateDialog} onClose={() => setOpenEscalateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Escalate Deficiency</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {selectedItem && (
              <Alert severity="warning" sx={{ mb: 3 }}>
                Escalating: {selectedItem.deficiency.description}
              </Alert>
            )}
            
            <Autocomplete
              fullWidth
              options={users}
              getOptionLabel={(option) => `${option.name} (${option.email})`}
              value={users.find(u => u._id === escalateData.escalatedTo) || null}
              onChange={(event, newValue) => {
                setEscalateData(prev => ({ ...prev, escalatedTo: newValue?._id || '' }))
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Escalate To"
                  placeholder="Select user to escalate to"
                  required
                />
              )}
              sx={{ mb: 3 }}
            />
            
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Escalation Reason"
              value={escalateData.escalationReason}
              onChange={(e) => setEscalateData(prev => ({ ...prev, escalationReason: e.target.value }))}
              placeholder="Explain why this deficiency needs to be escalated..."
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEscalateDialog(false)}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleEscalate}>
            Escalate
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default Checklist